import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Chip,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Container,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as EyeIcon,
  Edit as EditIcon,
  AssignmentTurnedIn as AssignIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  CheckCircle as CompleteIcon,
  MoneyOff as RefundIcon,
} from '@mui/icons-material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import jobService from '../services/api/jobService';

interface Job {
  id: string;
  title: string;
  client: string;
  artisan: string;
  budget: number;
  agreed_final_price?: number;
  escrow_amount?: number;
  artisan_payout_amount?: number;
  pricing_status?: string;
  status: 'pending' | 'accepted' | 'in_progress' | 'completed' | 'cancelled' | 'disputed';
  created_date: string;
  category?: string;
  category_id?: string;
  client_phone?: string;
  artisan_phone?: string;
  budget_min?: number;
  location?: string;
}

interface ArtisanOption {
  id: string;
  name: string;
  email: string;
  phone?: string;
  rating?: number;
  is_verified?: boolean;
}

interface JobStats {
  totalActive: number;
  completedThisWeek: number;
  disputedJobs: number;
  completionRate: number;
  statusCounts: {
    pending: number;
    accepted: number;
    in_progress: number;
    completed: number;
    disputed: number;
    cancelled: number;
  };
}

export default function JobsPage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [stats, setStats] = useState<JobStats>({
    totalActive: 0,
    completedThisWeek: 0,
    disputedJobs: 0,
    completionRate: 0,
    statusCounts: {
      pending: 0,
      accepted: 0,
      in_progress: 0,
      completed: 0,
      disputed: 0,
      cancelled: 0,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openReassignDialog, setOpenReassignDialog] = useState(false);
  const [reassignArtisan, setReassignArtisan] = useState(''); // stores artisan profile UUID
  const [artisanOptions, setArtisanOptions] = useState<ArtisanOption[]>([]);
  const [artisanOptionsLoading, setArtisanOptionsLoading] = useState(false);
  const [totalJobs, setTotalJobs] = useState(0);

  // Edit dialog
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editJob, setEditJob] = useState<Job | null>(null);
  const [editForm, setEditForm] = useState({ title: '', status: '', budget_max: 0, location: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Delete dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Refund dialog
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' });
  const [refundSuccess, setRefundSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadJobs();
  }, [paginationModel, searchQuery, statusFilter, categoryFilter, locationFilter]);

  const calculateStats = (jobList: Job[]) => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const statusCounts = {
      pending: jobList.filter((j) => j.status === 'pending').length,
      accepted: jobList.filter((j) => j.status === 'accepted').length,
      in_progress: jobList.filter((j) => j.status === 'in_progress').length,
      completed: jobList.filter((j) => j.status === 'completed').length,
      disputed: jobList.filter((j) => j.status === 'disputed').length,
      cancelled: jobList.filter((j) => j.status === 'cancelled').length,
    };

    const completedThisWeek = jobList.filter(
      (j) => j.status === 'completed' && new Date(j.created_date) >= oneWeekAgo
    ).length;

    const totalActive = statusCounts.pending + statusCounts.accepted + statusCounts.in_progress;
    const disputedJobs = statusCounts.disputed;
    const completionRate =
      jobList.length > 0
        ? Math.round((statusCounts.completed / jobList.length) * 100)
        : 0;

    return {
      totalActive,
      completedThisWeek,
      disputedJobs,
      completionRate,
      statusCounts,
    };
  };

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch jobs from API with search and status filter
      const response = await jobService.getJobs(
        statusFilter || undefined,
        searchQuery || undefined,
        500 // fetch more to allow for additional client-side filtering
      );

      // Transform API response to Job format
      let formattedJobs = (response.jobs || []).map((job: any) => ({
        id: job.id,
        title: job.title,
        client: job.client_name || 'Unknown',
        artisan: job.artisan_name || 'TBD',
        budget: parseFloat(job.budget_max) || 0,
        agreed_final_price: parseFloat(job.agreed_final_price) || 0,
        escrow_amount: parseFloat(job.escrow_amount) || 0,
        artisan_payout_amount: parseFloat(job.artisan_payout_amount) || 0,
        pricing_status: job.pricing_status || '',
        budget_min: parseFloat(job.budget_min) || 0,
        status: job.status,
        created_date: job.created_at,
        category: job.service_category || '',
        category_id: job.service_category_id || '',
        client_phone: job.client_phone,
        artisan_phone: job.artisan_phone,
        location: job.location || '',
      }));

      // Apply additional filters (category and location are not in API yet)
      if (categoryFilter) {
        formattedJobs = formattedJobs.filter((job) =>
          job.category?.toLowerCase().includes(categoryFilter.toLowerCase())
        );
      }

      if (locationFilter) {
        formattedJobs = formattedJobs.filter((job) =>
          job.location?.toLowerCase().includes(locationFilter.toLowerCase())
        );
      }

      // Calculate stats
      const jobStats = calculateStats(formattedJobs);
      setStats(jobStats);
      setTotalJobs(formattedJobs.length);

      // Apply pagination (server-side would be ideal, but API doesn't support offset/limit yet)
      const start = paginationModel.page * paginationModel.pageSize;
      const paginatedJobs = formattedJobs.slice(
        start,
        start + paginationModel.pageSize
      );

      setJobs(paginatedJobs);
    } catch (err: any) {
      console.error('Error loading jobs:', err);
      setError(err.response?.data?.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (job: Job) => {
    setSelectedJob(job);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedJob(null);
  };

  const handleOpenReassignDialog = async () => {
    setReassignArtisan('');
    setArtisanOptions([]);
    setOpenReassignDialog(true);
    // Fetch artisans filtered by the job's service category
    setArtisanOptionsLoading(true);
    try {
      const result = await jobService.getArtisans(selectedJob?.category_id || undefined);
      setArtisanOptions(result.artisans || []);
    } catch (err) {
      console.error('Failed to load artisans:', err);
    } finally {
      setArtisanOptionsLoading(false);
    }
  };

  const handleCloseReassignDialog = () => {
    setOpenReassignDialog(false);
    setReassignArtisan('');
    setArtisanOptions([]);
  };

  const handleReassignArtisan = async () => {
    if (!selectedJob || !reassignArtisan) return;

    try {
      await jobService.reassignArtisan(selectedJob.id, reassignArtisan);
      // Update displayed artisan name with the selected artisan's name
      const selectedArtisanObj = artisanOptions.find((a) => a.id === reassignArtisan);
      setSelectedJob({ ...selectedJob, artisan: selectedArtisanObj?.name || reassignArtisan });
      setOpenReassignDialog(false);
      loadJobs();
    } catch (err: any) {
      console.error('Error reassigning artisan:', err);
      setError(err.response?.data?.message || 'Failed to reassign artisan');
    }
  };

  // ── Edit handlers ──────────────────────────────────────────────────────────
  const handleOpenEdit = (job: Job, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditJob(job);
    setEditForm({
      title: job.title,
      status: job.status,
      budget_max: job.budget,
      location: job.location || '',
    });
    setOpenEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editJob) return;
    setEditLoading(true);
    try {
      await jobService.updateJob(editJob.id, {
        title: editForm.title,
        status: editForm.status,
        budget_max: editForm.budget_max,
        location: editForm.location,
      });
      setOpenEditDialog(false);
      loadJobs();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update job');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete handlers ─────────────────────────────────────────────────────────
  const handleOpenDelete = (job: Job, e: React.MouseEvent) => {
    e.stopPropagation();
    setJobToDelete(job);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!jobToDelete) return;
    setDeleteLoading(true);
    try {
      await jobService.deleteJob(jobToDelete.id);
      setOpenDeleteDialog(false);
      setJobToDelete(null);
      loadJobs();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete job');
      setOpenDeleteDialog(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Refund handlers ──────────────────────────────────────────────────────────
  const handleOpenRefund = () => {
    if (!selectedJob) return;
    setRefundForm({ amount: String(selectedJob.budget || ''), reason: '' });
    setRefundSuccess(null);
    setOpenRefundDialog(true);
  };

  const handleConfirmRefund = async () => {
    if (!selectedJob) return;
    if (!refundForm.reason.trim()) {
      alert('Please enter a reason for the refund.');
      return;
    }
    setRefundLoading(true);
    try {
      await jobService.processRefund(
        selectedJob.id,
        refundForm.amount ? parseFloat(refundForm.amount) : undefined,
        refundForm.reason
      );
      setRefundSuccess(`Refund of ₦${Number(refundForm.amount).toLocaleString()} processed for "${selectedJob.title}"`);
      loadJobs();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to process refund');
      setOpenRefundDialog(false);
    } finally {
      setRefundLoading(false);
    }
  };

  const getStatusColor = (status: string): 'warning' | 'error' | 'success' | 'info' | 'default' => {
    const colors: Record<string, 'warning' | 'error' | 'success' | 'info' | 'default'> = {
      pending: 'warning',
      accepted: 'info',
      in_progress: 'primary' as any,
      completed: 'success',
      cancelled: 'error',
      disputed: 'error',
    };
    return colors[status] || 'default';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Pending',
      accepted: 'Accepted',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      disputed: 'Disputed',
    };
    return labels[status] || status;
  };

  // DataTable columns definition
  const columns: GridColDef[] = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EyeIcon />}
            onClick={(e) => { e.stopPropagation(); handleOpenDialog(params.row); }}
            sx={{ textTransform: 'none', minWidth: 0 }}
          >
            View
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
            onClick={(e) => handleOpenEdit(params.row, e)}
            sx={{ textTransform: 'none', minWidth: 0 }}
          >
            Edit
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={(e) => handleOpenDelete(params.row, e)}
            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0 }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
    {
      field: 'title',
      headerName: 'Job Title',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.category}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'client',
      headerName: 'Client',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.value}</Typography>
          {params.row.client_phone && (
            <Typography variant="caption" color="text.secondary">
              {params.row.client_phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'artisan',
      headerName: 'Artisan',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.value}</Typography>
          {params.row.artisan_phone && (
            <Typography variant="caption" color="text.secondary">
              {params.row.artisan_phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value)}
          variant="filled"
          size="small"
        />
      ),
    },
    {
      field: 'budget',
      headerName: 'Budget',
      width: 130,
      align: 'right' as const,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || 'N/A'}</Typography>
      ),
    },
    {
      field: 'created_date',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Title */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkIcon sx={{ fontSize: 32 }} />
          Jobs Management
        </Typography>
      </Box>

      {/* Metric Cards - Top Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Active Jobs"
            value={stats.totalActive}
            icon={<WorkIcon />}
            color="primary"
            trend={5.2}
            trendDirection="up"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed This Week"
            value={stats.completedThisWeek}
            icon={<CompleteIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Disputed Jobs"
            value={stats.disputedJobs}
            icon={<DeleteIcon />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completion Rate"
            value={`${stats.completionRate}%`}
            subtitle="Overall completion rate"
            color="info"
            trend={stats.completionRate > 75 ? 8 : -2}
            trendDirection={stats.completionRate > 75 ? 'up' : 'down'}
          />
        </Grid>
      </Grid>

      {/* Status Overview */}
      <Card sx={{ mb: 4, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Status Overview
        </Typography>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`Pending: ${stats.statusCounts.pending}`}
              color="warning"
              variant="filled"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`Accepted: ${stats.statusCounts.accepted}`}
              color="info"
              variant="filled"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`In Progress: ${stats.statusCounts.in_progress}`}
              color="primary"
              variant="filled"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`Completed: ${stats.statusCounts.completed}`}
              color="success"
              variant="filled"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`Disputed: ${stats.statusCounts.disputed}`}
              color="error"
              variant="filled"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`Cancelled: ${stats.statusCounts.cancelled}`}
              color="default"
              variant="filled"
            />
          </Box>
        </Stack>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filter Card */}
      <Card sx={{ mb: 3, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search jobs, clients, artisans..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="disputed">Disputed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              placeholder="e.g., Plumbing"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              placeholder="e.g., Lagos"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('');
                setCategoryFilter('');
                setLocationFilter('');
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* DataTable */}
      <DataTable
        title="Jobs List"
        columns={columns}
        rows={jobs}
        rowCount={totalJobs}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        loading={loading}
        error={error}
        onSearchChange={(query) => {
          setSearchQuery(query);
          setPaginationModel({ ...paginationModel, page: 0 });
        }}
        searchPlaceholder="Search jobs..."
      />

      {/* Job Details Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkIcon />
          Job Details
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedJob && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Job Title
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {selectedJob.title}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Estimated Budget (Min)
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formatCurrency(selectedJob.budget_min || 0)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Estimated Budget (Max)
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {formatCurrency(selectedJob.budget)}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Agreed Final Price
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 700 }}>
                  {selectedJob.agreed_final_price ? formatCurrency(selectedJob.agreed_final_price) : 'Awaiting agreement'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Pricing Status
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {selectedJob.pricing_status || 'estimate_only'}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Escrow Funded
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {selectedJob.escrow_amount ? formatCurrency(selectedJob.escrow_amount) : 'Not funded'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Artisan Payout
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {selectedJob.artisan_payout_amount ? formatCurrency(selectedJob.artisan_payout_amount) : 'Pending'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Client Information
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{selectedJob.client}</strong>
                </Typography>
                {selectedJob.client_phone && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedJob.client_phone}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Assigned Artisan
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{selectedJob.artisan}</strong>
                </Typography>
                {selectedJob.artisan_phone && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedJob.artisan_phone}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Status
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={getStatusLabel(selectedJob.status)}
                    color={getStatusColor(selectedJob.status)}
                    variant="filled"
                    size="small"
                  />
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Category
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedJob.category || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Location
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {selectedJob.location || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Created Date
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {new Date(selectedJob.created_date).toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenReassignDialog}
            disabled={selectedJob?.status === 'completed'}
          >
            Reassign
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RefundIcon />}
            onClick={handleOpenRefund}
            disabled={
              selectedJob?.status === 'pending' ||
              selectedJob?.status === 'cancelled' ||
              selectedJob?.status === 'completed'
            }
          >
            Mark Refund
          </Button>
          <Button variant="contained" onClick={handleCloseDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Job Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon />
          Edit Job
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Title"
                value={editForm.title}
                onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.status}
                  label="Status"
                  onChange={(e) => setEditForm(f => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="disputed">Disputed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Budget Max (₦)"
                type="number"
                value={editForm.budget_max}
                onChange={(e) => setEditForm(f => ({ ...f, budget_max: parseFloat(e.target.value) || 0 }))}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={editForm.location}
                onChange={(e) => setEditForm(f => ({ ...f, location: e.target.value }))}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={editLoading || !editForm.title.trim()}
            startIcon={editLoading ? <CircularProgress size={16} /> : <EditIcon />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Job Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon />
          Delete Job
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to permanently delete:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700, mt: 1 }}>
            "{jobToDelete?.title}"
          </Typography>
          {jobToDelete?.status && ['accepted', 'in_progress'].includes(jobToDelete.status) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This job is currently <strong>{jobToDelete.status}</strong>. Cancel it first before deleting.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete Job
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Refund Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={openRefundDialog} onClose={() => { setOpenRefundDialog(false); setRefundSuccess(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, color: 'warning.dark' }}>
          <RefundIcon />
          Process Refund
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {refundSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>{refundSuccess}</Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                This will initiate a refund to the client and cancel the job. This action cannot be undone.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Job: <strong>{selectedJob?.title}</strong>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Refund Amount (₦)"
                    type="number"
                    value={refundForm.amount}
                    onChange={(e) => setRefundForm(f => ({ ...f, amount: e.target.value }))}
                    size="small"
                    helperText={`Full budget: ₦${Number(selectedJob?.budget || 0).toLocaleString()}. Leave as-is for full refund.`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason for Refund *"
                    multiline
                    rows={3}
                    value={refundForm.reason}
                    onChange={(e) => setRefundForm(f => ({ ...f, reason: e.target.value }))}
                    size="small"
                    placeholder="e.g. Client cancelled before work started, artisan failed to show up..."
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => { setOpenRefundDialog(false); setRefundSuccess(null); }}>
            {refundSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!refundSuccess && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleConfirmRefund}
              disabled={refundLoading || !refundForm.reason.trim()}
              startIcon={refundLoading ? <CircularProgress size={16} /> : <RefundIcon />}
            >
              {refundLoading ? 'Processing...' : 'Confirm Refund'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Reassign Artisan Dialog */}
      <Dialog open={openReassignDialog} onClose={handleCloseReassignDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Reassign Artisan</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Job: <strong>{selectedJob?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Category: <strong>{selectedJob?.category || 'N/A'}</strong> &nbsp;|&nbsp; Current Artisan: <strong>{selectedJob?.artisan}</strong>
          </Typography>

          {artisanOptionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : artisanOptions.length === 0 ? (
            <Alert severity="info">
              No artisans found{selectedJob?.category ? ` for the "${selectedJob.category}" category` : ''}. The artisan may need to be registered first.
            </Alert>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel>Select Artisan</InputLabel>
              <Select
                value={reassignArtisan}
                label="Select Artisan"
                onChange={(e) => setReassignArtisan(e.target.value)}
              >
                {artisanOptions.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {a.name} {a.is_verified ? '✓' : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {a.email}{a.rating ? ` · ★ ${a.rating}` : ''}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseReassignDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReassignArtisan}
            disabled={!reassignArtisan || artisanOptionsLoading}
            startIcon={<AssignIcon />}
          >
            Confirm Reassignment
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
